import {Action, getModule, Module, Mutation, MutationAction, VuexModule} from 'vuex-module-decorators';
import store from '@/model/store/store';
import lgcApi from '@/model/api';
import {GCIContentsListRes, GCIContentsInfoRes} from '@/model/dto/res/contents/get-category-list-res';
import {SearchQueryInterface} from '@/model/interfaces/search-interface';
import {PUCDataReq, PostUpsertContentsReq} from '@/model/dto/req/contents/post-upsert-contents-req';
import {PostUpsertContentsRes, PostUpsertContentsInfoRes} from '@/model/dto/res/contents/post-upsert-contents-res';
import {PUICDataReq, PostUploadImageContentsReq} from '@/model/dto/req/contents/post-upload-image-contents-req';
import {PostUploadImageContentsRes, PostUploadImageContentsInfoRes} from '@/model/dto/res/contents/post-upload-image-contents-res';
import {PDCDataReq, PostDeleteContentsReq} from '@/model/dto/req/contents/post-delete-contents-req';
import {PostDeleteContentsRes, PostDeleteContentsInfoRes} from '@/model/dto/res/contents/post-delete-contents-res';

@Module({dynamic: true, store, namespaced: true, name: 'ContentsModule'})
class ContentsModule extends VuexModule {

  public contents: string = ''; // ID
  public isAside: boolean = false;
  public isEditMode: boolean = false;
  public innerWidth: number = 0;
  public innerYOffset: number = 0;

  public contentsInfo: GCIContentsListRes[] = [];
  public contentsSearchInfo: GCIContentsListRes[] = [];
  public contentsSearchCurrentPage: number = 0;
  public contentsSearchTotalPages: number = 0;
  public contentsSearchTotalCount: number = 0;
  public upsertStatus: number = 0;
  public deleteStatus: number = 0;
  public uploadImageInfo: PostUploadImageContentsRes = {
    status: 0,
    message: '',
    path: ''
  };
  public panelNo: number | null = 0;
  public keywordHashtag: Array<{[key: string]: string | number}> = [];
  public targetHashtag: {[key: string]: string | number} = {};

  // Mutations
  @Mutation
  private setContents(contents: string): void {
    this.contents = contents;
  }

  @Mutation
  private setIsAside(status: boolean): void {
    this.isAside = status;
  }

  @Mutation
  private setIsEditMode(status: boolean): void {
    this.isEditMode = status;
  }

  @Mutation
  private setInnerWidth(width: number): void {
    this.innerWidth = width;
  }

  @Mutation
  private setPanelNo(val: number | null): void {
    this.panelNo = val;
  }

  @Mutation
  private setInnerYOffset(offset: number): void {
    this.innerYOffset = offset;
  }

  @Mutation
  private setClearContentsInfo(): void {
    this.contentsInfo = [];
  }

  @Mutation
  private setTargetHashtag(val: {[key: string]: string | number}): void {
    this.targetHashtag = val;
  }

  @Mutation
  private setKeywordHashtag(val: Array<{[key: string]: string | number}>): void {
    this.keywordHashtag = val;
  }

  @Mutation
  private setUpsertStatus(state: number) {
    this.upsertStatus = state;
  }

  // Actions
  /**
   * コンテンツの初期化
   *
   * @param param
   */
  @Action
  public initContents(
    param: {
      contents: string
    }
  ): void {
    this.setContents(param.contents);
  }

  @Action
  public pushIsAside(
    status: boolean
  ): void {
    this.setIsAside(status);
  }

  @Action
  public pushIsEditMode(
    status: boolean
  ): void {
    this.setIsEditMode(status);
  }

  @Action
  public pushInnerWidth(
    width: number
  ): void {
    this.setInnerWidth(width);
  }

  @Action
  public updatePanelNo(
    val: number | null
  ): void {
    this.setPanelNo(val);
  }

  @Action
  public pushInnerYOffset(
    offset: number
  ): void {
    this.setInnerYOffset(offset);
  }

  @Action
  public clearContentsInfo(): void {
    this.setClearContentsInfo();
  }

  @Action
  public updateTargetHashtag(val: {[key: string]: string | number}): void {
    this.setTargetHashtag(val);
  }

  @Action
  public updateKeywordHashtag(val: Array<{[key: string]: string | number}>): void {
    this.setKeywordHashtag(val);
  }

  @Action
  public updateUpsertStatus(state: number) {
    this.setUpsertStatus(state);
  }

  /**
   * コンテンツ情報をリクエスト
   *
   * TODO:
   */
  @MutationAction({ rawError: true })
  public async requestContentsInfo(param: SearchQueryInterface): Promise<any> {
    try {
      const info = await lgcApi.getContentsInfo(param);
      return {
        contentsInfo: info.contents
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * メモ情報追加をリクエスト
   */
  @MutationAction({ rawError: true })
  public async requestUpsertContentsInfo(
    param: PUCDataReq
  ): Promise<any> {
    try {
      const result = await lgcApi.postUpsertContentsInfo(param);
      let status: number = result?.results.status ?? 0;
      if (status === 0) { status = Math.floor( Math.random() * 101 ); }
      return {
        upsertStatus: status
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * コンテンツ内の画像アップロードをリクエスト
   */
  @MutationAction({ rawError: true })
  public async requestUploadImageContentsInfo(
    param: {
      file: File,
      contentId: string
    }
  ): Promise<any> {
    try {
      const res = await lgcApi.postUploadImageContentsInfo(param);
      return {
        uploadImageInfo: res.results
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * メモ情報削除をリクエスト
   */
  @MutationAction({ rawError: true })
  public async requestDeleteContentsInfo(
    param: PDCDataReq
  ): Promise<any> {
    try {
      const result = await lgcApi.postDeleteContentsInfo(param);
      let status: number = result?.results.status ?? 0;
      if (status === 0) { status = Math.floor( Math.random() * 101 ); }
      return {
        deleteStatus: status
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * コンテンツ情報をリクエスト
   *
   * TODO:
   */
  @MutationAction({ rawError: true })
  public async requestContentsSearchInfo(param: SearchQueryInterface): Promise<any> {
    try {
      const info = await lgcApi.getContentsSearchInfo(param);
      return {
        contentsSearchInfo: info.contents,
        contentsSearchCurrentPage: info.currentPage,
        contentsSearchTotalPages: info.totalPages,
        contentsSearchTotalCount: info.totalCount
      };
    } catch (err) {
      throw err;
    }
  }

}

export default getModule(ContentsModule, store);
